import { cn } from '@/lib/utils';

const STATE_STYLE: Record<string, string> = {
  draft: 'bg-line-soft text-muted',
  in_review: 'bg-state-warning-bg text-state-warning-text',
  working_copy: 'bg-line-soft text-muted',
  baselined: 'bg-electric/12 text-brand-blue',
  approved: 'bg-state-success-bg text-state-success-text',
  superseded: 'bg-state-warning-bg text-state-warning-text',
  obsolete: 'bg-line-soft text-muted line-through',
};
const STATE_LABEL: Record<string, string> = {
  draft: 'Draft',
  in_review: 'In review',
  working_copy: 'Working Copy',
  baselined: 'Baselined',
  approved: 'Approved',
  superseded: 'Superseded',
  obsolete: 'Obsolete',
};

export function LifecycleBadge({ state }: { state: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold',
        STATE_STYLE[state] ?? STATE_STYLE.draft,
      )}
    >
      {STATE_LABEL[state] ?? state}
    </span>
  );
}

// Order the breakdown so the spread reads done → in-progress → retired: approved and
// baselined first, then the active states, then superseded/obsolete.
const BREAKDOWN_ORDER = ['approved', 'baselined', 'in_review', 'working_copy', 'draft', 'superseded', 'obsolete'];

/** The per-state spread of a rolled-up document's markets — e.g. "2 Approved · 1 In
 *  review · 1 Draft" — as compact count-chips, shown instead of one collapsed badge. */
export function MarketStateBreakdown({ states }: { states: { state: string; count: number }[] }) {
  const ordered = [...states].sort(
    (a, b) => BREAKDOWN_ORDER.indexOf(a.state) - BREAKDOWN_ORDER.indexOf(b.state),
  );
  return (
    <div className="flex flex-wrap justify-center gap-1">
      {ordered.map((s) => (
        <span
          key={s.state}
          className={cn(
            'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] font-semibold',
            STATE_STYLE[s.state] ?? STATE_STYLE.draft,
          )}
        >
          <span className="tabular-nums">{s.count}</span>
          {STATE_LABEL[s.state] ?? s.state}
        </span>
      ))}
    </div>
  );
}
